// 反馈弹窗
import { useState, FC, forwardRef, useImperativeHandle, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { contrast } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import { getSelection } from '@/common/api/requirement';
import { post } from '@/common/request';
import { refactorSelection } from '@/common/utils/ways';
import styles from './index.module.less';
import { Modal, Form, Row, Col, message, Empty } from 'antd';
import FormEmployee from '@/common/components/business/Form/FormEmployee';
import Fuzzy from '@/common/components/Form/Fuzzy/Fuzzy';
import FormScreenShot from 'src/common/components/Form/FormScreenShot';
import V2Form from '../../Form/V2Form';
import V2FormCheckbox from '../../Form/V2FormCheckbox/V2FormCheckbox';
import V2FormRadio from '../../Form/V2FormRadio/V2FormRadio';
import V2FormTextArea from '../../Form/V2FormTextArea/V2FormTextArea';

const Feedback:FC<{title:string, ref?:any}> = forwardRef(({ title = '' }, ref) => {
  const FEEDBACK_TYPE_ROAST = 1; // 反馈类型 吐槽
  // const FEEDBACK_TYPE_ERROR = 2; // 反馈类型 报错

  const userInfo = useSelector((state: any) => state.common.userInfo);
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm();
  const type = Form.useWatch('type', form);
  const targetIds = Form.useWatch('targetIds', form);
  const [loading, setLoading] = useState(false);
  const placeFuzzyRef = useRef<any>();

  const [selection, setSelection] = useState({
    types: [], // 类型
    problemFeedbackLabels: [] // 快捷标签
  });


  useImperativeHandle(ref, () => ({
    init: methods.init
  }));

  const methods = useMethods({
    init() {
      setVisible(true);
      form.setFieldsValue(Object.assign(methods.getFormData(), {
        position: title,
        initiatorId: userInfo?.id
      }));
      targetIds?.length && loadPlaceOptions();
    },
    handleOk() {
      form.validateFields().then(result => {
        const params = Object.assign(result, { position: title, positionUrl: window.location.href });
        setLoading(true);
        post('/advice/create', params).then(() => {
          message.success('反馈成功');
          methods.cancel();
        }).finally(() => {
          setLoading(false);
        });
      });

    },
    getFormData() {
      return {
        position: null, // 页面名称
        type: FEEDBACK_TYPE_ROAST, // 反馈类型-默认选中业务投诉
        detailTypeIds: [], // 快捷标签
        targetIds: [], // 吐槽对象
        placeIds: [], // 投诉项目
        initiatorId: null, // 发起人
        remark: '', // 问题描述
        pictures: [], // 截图文件
      };
    },
    cancel() {
      setVisible(false);
    },
    complete() {
      setVisible(true);
    },
    getSelection() {
      getSelection({ modules: 'problemFeedbackLabel,adviceType' }).then((res) => {
        setSelection(val => ({ ...val,
          // 产品现在不需要资源纠错类型，故过滤掉原有的资源纠错（id=2）类型
          types: contrast(res, 'adviceType', []).filter((item: any) => item.id !== 2),
          problemFeedbackLabels: contrast(res, 'problemFeedbackLabels', []),
        }));
      });
    }
  });

  useEffect(() => {
    methods.getSelection();
  }, []);

  const loadPlaceOptions = useCallback(async (name: string = '') => {
    const ids = form.getFieldValue('targetIds');
    if (!ids || !ids.length) {
      placeFuzzyRef.current?.setOptions?.([], true);
      return [];
    }
    const res = await post('/advice/places', { keyword: name, employeeIds: ids });
    const list = res?.objectList || [];
    placeFuzzyRef.current?.setOptions?.(list, true);
    return list;
  }, [form]);

  // 选择投诉对象后，自动拉取对应投诉项目
  useEffect(() => {
    if (targetIds && targetIds.length) {
      loadPlaceOptions();
    } else {
      form.setFieldsValue({ placeIds: [] });
      placeFuzzyRef.current?.setOptions?.([], true);
    }
  }, [targetIds, loadPlaceOptions, form]);


  return (<Modal
    title='反馈'
    width='610px'
    open={visible}

    onOk={methods.handleOk}
    onCancel={() => methods.cancel()}
    zIndex={1001}
    okText='提交'
    confirmLoading={loading}
  >
    <V2Form form={form}>
      <div className={styles.feedbackTitle}>定位：{ title || '' }</div>
      <Row gutter={24}>
        <Col span={12}>
          <V2FormRadio label='类型' name='type' options={refactorSelection(selection.types)} required/>
        </Col>
        <Col span={12}>
          {type === FEEDBACK_TYPE_ROAST && <FormEmployee
            form={form}
            label='投诉对象'
            name='targetIds'
            config={{ immediateOnce: true, mode: 'multiple', debounceTimeout: 300 }}
            rules={[{ required: true, message: '请选择员工' }]}
          />}
        </Col>
      </Row>
      {type === FEEDBACK_TYPE_ROAST && (
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              name='placeIds'
              label='投诉项目'
              rules={[{ required: true, message: '请选择投诉项目' }]}
            >
              <Fuzzy
                ref={placeFuzzyRef}
                mode='multiple'
                placeholder='请输入项目名称，并选择'
                loadData={loadPlaceOptions}
                onDropdownVisibleChange={(open) => {
                  if (open) {
                    loadPlaceOptions();
                  }
                }}
                renderEmptyReactNode={!targetIds?.length
                  ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='请先选择投诉对象' />
                  : undefined}
                fieldNames={{ label: 'name', value: 'id' }}
                optionLabelProp='label'
                allowClear
                immediateOnce={false}
                needCacheSelect
                debounceTimeout={300}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <FormEmployee
              form={form}
              label='发起人'
              name='initiatorId'
              config={{ immediateOnce: true }}
              rules={[{ required: true, message: '请选择发起人' }]}
              placeholder='请输入发起人关键词，并选择'
              defaultOptions={userInfo?.id ? [{ id: userInfo.id, name: userInfo.name }] : undefined}
            />
          </Col>
        </Row>
      )}
      <Row gutter={24}>
        <Col span={24}>
          {type === FEEDBACK_TYPE_ROAST && <V2FormCheckbox
            label='常见问题'
            name='detailTypeIds'
            options={refactorSelection(selection.problemFeedbackLabels)}
          />}
        </Col>
        <Col span={24}>
          <V2FormTextArea
            label='描述问题'
            name='remark'
            maxLength={500}
            // placeholder={type === FEEDBACK_TYPE_ROAST ? '请描述业务流程及发生的问题，可截图问题页面以便更好定位' : '请告知错误的信息以及应修改为的正确信息，可截图问题页面以便更好定位'}
            placeholder='请描述业务流程及发生的问题，可截图问题页面以便更好定位'
            config={{ showCount: true }}
            required
          />
          <div className={styles.screenShotButton}>
            <FormScreenShot
              label=''
              name='pictures'
              config={{ onStart: methods.cancel, duration: 400, onCancel: methods.complete }}
              onChange={methods.complete}
            />
          </div>
        </Col>
      </Row>
    </V2Form>
  </Modal>);
});

export default Feedback;
